import {
  getFileType,
  isLargeStreamedPdf,
} from "../Document/DocumentUtils";
import { saveDocumentAnnotations } from "../../service/matter";

const ANNOTATION_CHANGE_DEBOUNCE_MS = 300;
const RETRY_DELAYS_MS = [500, 1500];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Bind `annotationChanged` and `fieldChanged` on a PDF WebViewer instance.
 *
 * - Sends XFDF deltas with the string UUID `document_id` over WebSocket and
 *   persists them durably via `POST /api/documents/<id>/annotations/`.
 * - Retains any exported `xfdfCommand` across transient HTTP errors and
 *   retries automatically; if still unsaved, keeps the delta buffered so a
 *   subsequent edit or tab-close flush never drops it after Apryse's
 *   `exportAnnotationCommand()` delta buffer has been drained.
 * - For small PDFs, also calls `handleAutoSave` (full-PDF export+upload).
 * - For large streamed PDFs (RBAs, 100+ pages, 15MB+), skips full-PDF
 *   client export (`exportAnnotations()` + `doc.getFileData()`) so the
 *   viewer stays in minimum-downloads range mode and never pulls hundreds
 *   of megabytes into the browser's 32-bit WASM heap.
 * - Provides `flush()` so pending edits are saved before a tab closes.
 */
export const attachAnnotationPersistenceListeners = ({
  instance,
  docId,
  docData,
  docRecord,
  documentDetail,
  currentUserName,
  userId,
  isApplyingRemoteUpdateRef,
  annotationChangeDebounceRef,
  annotationListenersMapRef,
  annotationListenerRef,
  dispatch,
  sendWebSocketMessage,
  saveAnnotationsApi = saveDocumentAnnotations,
  retryDelaysMs = RETRY_DELAYS_MS,
  handleAutoSave,
  handleAutoSaveStart,
  showAutoSaveIndicator,
  handleAutoSaveError,
}) => {
  if (!instance?.Core || !docId) return null;
  const { annotationManager, documentViewer } = instance.Core;
  if (!annotationManager) return null;

  const rawFileType =
    docData?.file_type ||
    docRecord?.file_type ||
    docData?.name ||
    docRecord?.name ||
    "pdf";
  const fileType = getFileType(rawFileType);
  if (fileType !== "pdf" && fileType !== "unknown") return null;

  const docKey = String(docId);

  const previousEntry = annotationListenersMapRef?.current?.get(docKey);
  if (previousEntry?.annotationManager) {
    try {
      if (previousEntry.handler) {
        previousEntry.annotationManager.removeEventListener(
          "annotationChanged",
          previousEntry.handler
        );
      }
      if (previousEntry.fieldChangeHandler) {
        previousEntry.annotationManager.removeEventListener(
          "fieldChanged",
          previousEntry.fieldChangeHandler
        );
      }
    } catch (_) {}
  }

  if (currentUserName && typeof annotationManager.setCurrentUser === "function") {
    try {
      annotationManager.setCurrentUser(currentUserName);
    } catch (_) {}
  }

  let pendingTimer = null;
  let inFlightPromise = null;
  // Commands drained from `exportAnnotationCommand()` that have not yet been
  // acknowledged by `saveAnnotationsApi` — retained so a failed request can
  // retry or flush without losing the delta.
  const unsentCommands = [];

  const checkIsLargePdf = () =>
    typeof isLargeStreamedPdf === "function" &&
    isLargeStreamedPdf({
      documentViewer,
      documentData: docData,
      documentRecord: docRecord,
      documentName: docData?.name || docRecord?.name,
    });

  const postWithRetry = async (documentIdForWS, payload) => {
    if (typeof saveAnnotationsApi !== "function") return;
    let lastError;
    const totalAttempts = 1 + (Array.isArray(retryDelaysMs) ? retryDelaysMs.length : 0);
    for (let attempt = 0; attempt < totalAttempts; attempt += 1) {
      try {
        await saveAnnotationsApi(documentIdForWS, payload);
        return;
      } catch (err) {
        lastError = err;
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) {
          throw err;
        }
        if (attempt < totalAttempts - 1) {
          const delay = retryDelaysMs[attempt] || 0;
          if (delay > 0) await sleep(delay);
        }
      }
    }
    throw lastError;
  };

  const sendAnnotationUpdate = async ({
    manageIndicator = false,
    showSuccessToast = true,
  } = {}) => {
    const runSave = (async () => {
      try {
        const documentIdForWS = String(docId || documentDetail?.id);
        const resolvedUserId =
          userId !== undefined
            ? userId
            : Number(localStorage.getItem("user_id"));

        const xfdfCommand = await annotationManager.exportAnnotationCommand();
        if (xfdfCommand && String(xfdfCommand).trim()) {
          unsentCommands.push(String(xfdfCommand));
        }

        if (unsentCommands.length === 0) {
          return;
        }

        let pageCount;
        try {
          const count = documentViewer?.getPageCount?.();
          if (Number.isFinite(count) && count > 0) pageCount = count;
        } catch (_) {}

        while (unsentCommands.length > 0) {
          const nextCommand = unsentCommands[0];

          if (dispatch && sendWebSocketMessage) {
            dispatch(
              sendWebSocketMessage({
                type: "annotation_change",
                document_id: documentIdForWS,
                xfdf_command: nextCommand,
                user_id: resolvedUserId,
                user_name: currentUserName || undefined,
                page_count: pageCount,
              })
            );
          }

          await postWithRetry(documentIdForWS, {
            xfdf_command: nextCommand,
            user_id: resolvedUserId,
            user_name: currentUserName || undefined,
            page_count: pageCount,
          });

          unsentCommands.shift();
        }

        if (manageIndicator && showSuccessToast) {
          showAutoSaveIndicator?.(docData?.name || docRecord?.name);
        }
      } catch (error) {
        console.error("[ANNOTATIONS] Failed to save annotation/form change:", error);
        if (manageIndicator) {
          handleAutoSaveError?.(error, docData?.name || docRecord?.name);
        }
      }
    })();
    inFlightPromise = runSave;
    try {
      await runSave;
    } finally {
      if (inFlightPromise === runSave) {
        inFlightPromise = null;
      }
    }
  };

  const scheduleAnnotationUpdate = ({ manageIndicator = false } = {}) => {
    if (pendingTimer) clearTimeout(pendingTimer);
    const debounce = annotationChangeDebounceRef?.current;
    if (debounce?.timerId) clearTimeout(debounce.timerId);
    if (debounce) debounce.documentId = docId;
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      if (debounce && debounce.documentId === docId) {
        debounce.timerId = null;
      }
      sendAnnotationUpdate({ manageIndicator, showSuccessToast: true });
    }, ANNOTATION_CHANGE_DEBOUNCE_MS);
    if (debounce) debounce.timerId = pendingTimer;
  };

  const flush = async ({ showSuccessToast = false } = {}) => {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
      const debounce = annotationChangeDebounceRef?.current;
      if (debounce && debounce.documentId === docId) {
        debounce.timerId = null;
      }
    }
    if (inFlightPromise) {
      await inFlightPromise.catch(() => {});
    }
    await sendAnnotationUpdate({
      manageIndicator: checkIsLargePdf(),
      showSuccessToast,
    });
  };

  const annotationChangeHandler = async (annotations, action, options) => {
    if (
      options?.imported ||
      isApplyingRemoteUpdateRef?.current ||
      instance.__whgImportingAnnotations
    ) {
      return;
    }
    if (["add", "modify", "delete"].includes(action)) {
      const largePdf = checkIsLargePdf();
      if (!largePdf) {
        handleAutoSave?.(
          documentViewer,
          annotationManager,
          true,
          docId,
          docData?.name
        );
      } else {
        handleAutoSaveStart?.();
      }
      scheduleAnnotationUpdate({ manageIndicator: largePdf });
    }
  };

  const fieldChangeHandler = () => {
    if (
      isApplyingRemoteUpdateRef?.current ||
      instance.__whgImportingAnnotations
    ) {
      return;
    }
    const largePdf = checkIsLargePdf();
    if (!largePdf) {
      handleAutoSave?.(
        documentViewer,
        annotationManager,
        true,
        docId,
        docData?.name
      );
    } else {
      handleAutoSaveStart?.();
    }
    scheduleAnnotationUpdate({ manageIndicator: largePdf });
  };

  annotationManager.addEventListener("annotationChanged", annotationChangeHandler);
  annotationManager.addEventListener("fieldChanged", fieldChangeHandler);

  const listenerEntry = {
    handler: annotationChangeHandler,
    fieldChangeHandler,
    flush,
    documentId: docId,
    annotationManager,
    documentViewer,
  };
  annotationListenersMapRef?.current?.set(docKey, listenerEntry);
  if (annotationListenerRef) {
    annotationListenerRef.current = listenerEntry;
  }
  return listenerEntry;
};
